import { useNavigate, useParams } from "react-router-dom";
import PageScaffold from "../../components/PageScaffold";
import { PLANS_PAGE_PATH } from "./PlansPage";
import { useContext, useEffect, useMemo } from "react";
import { ContextStatus } from "@leedpulse-monorepo/enums";
import { Button, Card, Flex, Select, Tag, Typography } from "antd";
import PlansContext from "../../contexts/data/PlansContext";
import { PlanCreationFormProvider } from "../../contexts/forms/plans/PlanCreationFormContext";
import PlanCreationForm from "../../components/plans_page/PlanCreationForm";
import { PlanEditionFormProvider } from "../../contexts/forms/plans/PlanEditionFormContext";
import PlanDetails from "../../components/plans_page/PlanDetails";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

interface PlanDetailPageProps {
  isCreateMode: boolean;
}

const PlanDetailPage: React.FC<PlanDetailPageProps> = ({
  isCreateMode,
}) => {
  const navigate = useNavigate();
  const plansContext = useContext(PlansContext);
  const planId = useParams().planId ?? '';

  const currentIndex = useMemo(() => {
    return plansContext.plansRefs.findIndex((item) => item.id === plansContext.selected?.id);
  }, [plansContext.plansRefs, plansContext.selected?.id]);

  const selectionMismatch =
    (!isCreateMode && plansContext.selected === null) ||
    (!isCreateMode && plansContext.selected?.id !== planId);

  const selectPrevNextPlan = (direction: 'prev' | 'next') => {
    const targetIndex = currentIndex + (direction === 'prev' ? -1 : 1);
    selectPlan(plansContext.plansRefs[targetIndex].id);
  };

  const selectPlan = async (id: string) => {
    await plansContext.fetchAndSelectById(id);
    navigate(`${PLANS_PAGE_PATH}/${id}`);
  };

  useEffect(() => {
    if (
      selectionMismatch &&
      plansContext.selectionStatus !== ContextStatus.LOADING
    ) {
      plansContext.fetchAndSelectById(planId);
    }
    return () => {
      plansContext.reset();
    };
  }, []);

  useEffect(() => {
    if (!selectionMismatch) {
      plansContext.fetchPlansRefs();
    }
  }, [selectionMismatch]);

  return (
    <PageScaffold
      title={isCreateMode ? "Nouveau plan" : 'Plan ' + (plansContext.selected?.name || '')}
      subtitle={
        isCreateMode
          ? "Ajoutez un nouveau plan"
          : "Consultez et modifiez les informations du plan"
      }
      breadcrumbItems={[
        {
          target: PLANS_PAGE_PATH,
          title: "Plans",
        },
        {
          title: isCreateMode ? "Nouveau" : plansContext.selected?.name || '',
        },
      ]}
    >
      <Flex align="center" gap={4} style={{ marginBottom: '0.625rem' }}>
        <Button
          type="text"
          icon={<LeftOutlined />}
          disabled={currentIndex <= 0}
          onClick={() => selectPrevNextPlan('prev')}
        />
        <Select
          value={plansContext.selected?.id}
          onChange={(value) => selectPlan(value)}
          options={plansContext.plansRefs
            .map((item) => ({ value: item.id, label: item.name, plan: item }))
          }
          optionRender={({ data }) => (
            <Typography.Text>{data.plan.name} <Tag color="green">{data.plan.price.formatted}</Tag> <Tag color="blue">{data.plan.credits.leedbucks}c</Tag> <Tag color="lightgrey">{data.plan.id}</Tag></Typography.Text>
          )}
          style={{ minWidth: 512 }}
        />
        <Button
          type="text"
          icon={<RightOutlined />}
          disabled={plansContext.plansRefs.length - 1 <= currentIndex}
          onClick={() => selectPrevNextPlan('next')}
        />
      </Flex>
      {
        isCreateMode ? (
          <PlanCreationFormProvider>
            <PlanCreationForm />
          </PlanCreationFormProvider>
        ) : selectionMismatch || plansContext.selected == null ? (
          <Card loading />
        ) : (
          <PlanEditionFormProvider key={plansContext.selected.id}>
            <PlanDetails planDoc={plansContext.selected} />
          </PlanEditionFormProvider>
        )
      }
    </PageScaffold >
  );
};

export default PlanDetailPage;
